import { useEffect, useRef, useState } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { equityPoints } from '@/app/lib/quant/liveView'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isLiveGone, quantErrorMessage } from '@/app/lib/quant/quantErrors'

export const EQUITY_MAX_POINTS = 500
export const EQUITY_POLL_MS = 60_000

export interface QuantLiveEquityState {
  /** `[UTC epoch seconds, equity]`, ascending; points with a null equity are dropped. */
  points: Array<[number, number]>
  total: number
  truncated: boolean
  problem: string | null
  /** Set while the latest read failed; the held points stay. */
  error?: string
  loaded: boolean
}

const INITIAL: QuantLiveEquityState = {
  points: [],
  total: 0,
  truncated: false,
  problem: null,
  loaded: false,
}

/**
 * Spec 041: the equity line of one session, thinned to 500 points by the server. Read on
 * open, then every 60 s while `active`, plus one final read when `active` turns false. A
 * failed read keeps the held points and backs off 2, 4, 8, 15 s; a non-null `problem` in a
 * good answer is shown by the caller and does not stop the cadence. Nothing is requested
 * while the tab is hidden; a read happens at once when it becomes visible.
 */
export function useQuantLiveEquity(id: string, active: boolean): QuantLiveEquityState {
  const [state, setState] = useState<QuantLiveEquityState>(INITIAL)
  const activeRef = useRef(active)
  const prevActive = useRef(active)
  const finalRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    activeRef.current = active
    if (prevActive.current && !active) finalRef.current?.()
    prevActive.current = active
  }, [active])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null
    let inFlight = false
    let failures = 0
    let done = false
    let finalPending = false

    const isHidden = () => document.visibilityState === 'hidden'
    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const schedule = (ms: number) => {
      clearTimer()
      if (cancelled || done || isHidden()) return
      timer = setTimeout(read, ms)
    }

    function read() {
      timer = null
      if (cancelled || inFlight || isHidden()) return
      finalPending = false
      inFlight = true
      const own = new AbortController()
      controller = own
      quantApi.getLiveEquity(id, EQUITY_MAX_POINTS, own.signal).then(
        (answer) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures = 0
          setState({
            points: equityPoints(Array.isArray(answer.points) ? answer.points : []),
            total: typeof answer.total === 'number' ? answer.total : 0,
            truncated: answer.truncated === true,
            problem: answer.problem ?? null,
            loaded: true,
          })
          if (finalPending) read()
          else if (activeRef.current) schedule(EQUITY_POLL_MS)
          else done = true
        },
        (error: unknown) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          if (isLiveGone(error)) {
            done = true
            setState((prev) => ({ ...prev, loaded: true }))
            return
          }
          failures += 1
          const message = quantErrorMessage(error)
          setState((prev) => ({ ...prev, error: message }))
          schedule(nextDelay(failures))
        },
      )
    }

    finalRef.current = () => {
      if (cancelled) return
      finalPending = true
      done = false
      clearTimer()
      if (!inFlight) read()
    }

    function onVisibility() {
      if (cancelled) return
      if (isHidden()) {
        clearTimer()
        return
      }
      if ((!done || finalPending) && !inFlight) {
        clearTimer()
        read()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    read()
    return () => {
      cancelled = true
      clearTimer()
      controller?.abort()
      finalRef.current = null
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [id])

  return state
}
